// Copyright © 2026 Alex Bruce. All rights reserved.
// Proprietary and confidential. Unauthorized copying, modification,
// or distribution is strictly prohibited.

// ── Schedule delta ──────────────────────────────────────────────────────────
//
// "Since we last met, what changed about *when*?" -- the sentence an owner
// actually asks for when a draw moves. Not a Gantt-vs-Gantt overlay; three
// facts: how long the job is now, which scopes moved and by how much, and
// where the peak month went.
//
// Snapshots carry the schedule only from 140fc4a on. An older snapshot is
// reported as not comparable rather than as "nothing moved".

import type { CostSchedule } from "./costSchedule";
import { buildCostLeaves, rollupSpan, spreadLeaves, fromISO, addDays } from "./costSchedule";

export interface ScheduleStateLike {
  duration?: number;
  schedule?: { projectStartDate?: string } | null;
  costSchedule?: CostSchedule | null;
  divisions?: any[];
  itemState?: Record<string, any>;
}

export interface ScopeMove {
  id: string;
  name: string;
  divCode: string;
  /** Positive = later, negative = earlier. */
  startShiftDays: number;
  endShiftDays: number;
}

export interface ScheduleDelta {
  /** False when the earlier state has no schedule to compare against. */
  comparable: boolean;
  durationFrom: number | null;
  durationTo: number | null;
  startFrom: string | null;
  startTo: string | null;
  /** Scopes whose span changed, largest shift first. */
  moved: ScopeMove[];
  unchanged: number;
  added: number;
  removed: number;
  /** "Mar 2027" -- month with the largest spend, each side. */
  peakFrom: string | null;
  peakTo: string | null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MS_DAY = 86_400_000;

function monthLabel(d: Date) {
  return `${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

export function hasSchedule(s: ScheduleStateLike | null | undefined): boolean {
  if (!s) return false;
  const cs = s.costSchedule ?? {};
  return Object.values(cs).some(e => e?.bands?.length) || !!s.schedule?.projectStartDate;
}

/** Each costed subcategory's resolved span, keyed by subcategory id. */
function spansByScope(s: ScheduleStateLike) {
  const out: Record<string, { name: string; divCode: string; start: string; end: string }> = {};
  const leavesBySub = buildCostLeaves(s.divisions ?? [], s.itemState ?? {});
  for (const div of s.divisions ?? []) {
    for (const sub of div.subcategories ?? []) {
      const span = rollupSpan(leavesBySub[sub.id] ?? [], s.costSchedule ?? {});
      if (span) out[sub.id] = { name: sub.name, divCode: div.code, ...span };
    }
  }
  return out;
}

/** Month with the largest projected spend, or null when nothing is scheduled. */
export function peakMonth(s: ScheduleStateLike): string | null {
  const startISO = s.schedule?.projectStartDate;
  if (!startISO) return null;
  const start = fromISO(startISO);
  const weeks = Math.max(1, Math.round((s.duration || 12) * 4.333));
  const leavesBySub = buildCostLeaves(s.divisions ?? [], s.itemState ?? {});
  const weekly = Array(weeks).fill(0) as number[];
  for (const leaves of Object.values(leavesBySub)) {
    spreadLeaves(leaves, s.costSchedule ?? {}, start, weeks).forEach((v, i) => { weekly[i] += v; });
  }
  const byMonth = new Map<string, number>();
  weekly.forEach((v, w) => {
    if (!v) return;
    const k = monthLabel(addDays(start, w * 7));
    byMonth.set(k, (byMonth.get(k) ?? 0) + v);
  });
  let best: string | null = null, bestV = 0;
  for (const [k, v] of byMonth) if (v > bestV) { best = k; bestV = v; }
  return best;
}

export function summarizeScheduleDelta(prev: ScheduleStateLike, curr: ScheduleStateLike): ScheduleDelta {
  const base: ScheduleDelta = {
    comparable: hasSchedule(prev),
    durationFrom: prev.duration ?? null,
    durationTo: curr.duration ?? null,
    startFrom: prev.schedule?.projectStartDate || null,
    startTo: curr.schedule?.projectStartDate || null,
    moved: [], unchanged: 0, added: 0, removed: 0,
    peakFrom: null, peakTo: null,
  };
  if (!base.comparable) return base;

  const a = spansByScope(prev), b = spansByScope(curr);
  for (const id of Object.keys(b)) {
    if (!a[id]) { base.added++; continue; }
    const ds = Math.round((fromISO(b[id].start).getTime() - fromISO(a[id].start).getTime()) / MS_DAY);
    const de = Math.round((fromISO(b[id].end).getTime() - fromISO(a[id].end).getTime()) / MS_DAY);
    if (ds === 0 && de === 0) base.unchanged++;
    else base.moved.push({ id, name: b[id].name, divCode: b[id].divCode, startShiftDays: ds, endShiftDays: de });
  }
  for (const id of Object.keys(a)) if (!b[id]) base.removed++;
  base.moved.sort((x, y) =>
    Math.max(Math.abs(y.startShiftDays), Math.abs(y.endShiftDays)) -
    Math.max(Math.abs(x.startShiftDays), Math.abs(x.endShiftDays)));

  base.peakFrom = peakMonth(prev);
  base.peakTo = peakMonth(curr);
  return base;
}

/** "6 wks earlier", "3 days later", "unchanged". Weeks from a fortnight up. */
export function describeShift(days: number): string {
  if (days === 0) return "unchanged";
  const dir = days > 0 ? "later" : "earlier";
  const n = Math.abs(days);
  if (n < 14) return `${n} day${n === 1 ? "" : "s"} ${dir}`;
  const w = Math.round(n / 7);
  return `${w} wk${w === 1 ? "" : "s"} ${dir}`;
}

/** True when nothing about the schedule differs. */
export function scheduleUnchanged(d: ScheduleDelta): boolean {
  return d.comparable
    && d.durationFrom === d.durationTo
    && d.startFrom === d.startTo
    && d.moved.length === 0 && d.added === 0 && d.removed === 0;
}
